import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, aeoGeo } from "@/lib/seo/schema";
import { buildRelatedHubs } from "@/lib/seo/related-hubs";
import { getCity } from "@/lib/seo/cities";
import { umeaTrust, umeaProductionPricing, umeaProcess } from "@/content/landing/umea";

const FAQ: LandingFAQ[] = [
  {
    "q": "Vad är skillnaden mellan filmproduktion och en filmbyrå?",
    "a": "Filmproduktion är hantverket. Filmbyrå inkluderar även strategi och kreativ idé. Fenny levererar båda i samma fasta pris."
  },
  {
    "q": "Hur ser en typisk filmproduktion i Umeå ut?",
    "a": "Brief (v 1) → manus & storyboard (v 2) → pre-prod (v 3) → inspelning 1–3 dagar (v 3–4) → klipp, grade, ljudmix (v 4–6) → leverans."
  },
  {
    "q": "Vad kostar filmproduktion i Umeå 2026?",
    "a": "Social-content: 40 000–120 000 kr. Reklamfilm 15–30 s: 150 000–450 000 kr. Brand-/industrifilm: 250 000–700 000 kr. Pulse: från 14 995 kr/mån."
  },
  {
    "q": "Hur snabbt kan ni vara igång?",
    "a": "Första möte inom 5 arbetsdagar. Pulse: första klipp inom 10–14 dagar."
  },
  {
    "q": "Var i Västerbotten filmar ni?",
    "a": "Hela regionen — Umeå universitet, SLU, Umeå Science Park, NUS, Holmsund och Sandviken samt Holmsund, Vännäs, Robertsfors och hela Västerbotten. Drönar-tillstånd för aktuella zoner."
  },
  {
    "q": "Vilken utrustning använder ni?",
    "a": "Sony FX6/FX9 eller ARRI Alexa Mini. Aputure/ARRI-ljus. DJI-drönare. Post i DaVinci Resolve och Premiere."
  },
  {
    "q": "Klarar ni krävande industri- och säkerhetsmiljöer?",
    "a": "Ja. Vana vid tech, forskning och skogsindustri. PPE, NDA, säkerhetsbriefer och samordning med skiftledare."
  },
  {
    "q": "Tar ni hand om manus och regi?",
    "a": "Ja. Vana att översätta komplex verksamhet till säljande film."
  },
  {
    "q": "Hur många versioner ingår?",
    "a": "En master per format. Engelska versioner för export-/forskningskommunikation vid behov."
  },
  {
    "q": "Får vi raw-materialet?",
    "a": "Standard: mastrade filer + stillbilder. Raw lagras 12 månader."
  },
  {
    "q": "Kan vi vara med på set?",
    "a": "Ja. Live-feed via Frame.io för distans-kunder."
  },
  {
    "q": "Hur fungerar betalning?",
    "a": "50% vid produktionsstart, 50% vid leverans."
  }
];

const CITY = getCity("umea")!;
const RELATED = buildRelatedHubs(CITY, "produktionsbolag");

export const Route = createFileRoute("/produktionsbolag_/umea")({
  head: () => ({
    meta: [
      { title: "Produktionsbolag Umeå — film & video | Fenny" },
      { name: "description", content: "Produktionsbolag i Umeå och Västerbotten med egen regi, foto och post. Reklamfilm, brand-film, kundcase — också white-label åt byråer." },
      { property: "og:title", content: "Produktionsbolag Umeå — Fenny" },
      { property: "og:description", content: "Produktionsbolag i Umeå med egen regi, foto och post." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/produktionsbolag/umea" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/filmproduktion/umea" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/produktionsbolag/umea", name: "Produktionsbolag Umeå — film & video | Fenny", description: "Produktionsbolag i Umeå och Västerbotten med egen regi, foto och post. Reklamfilm, brand-film, kundcase — också white-label åt byråer."})) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessForCity({ city: "Umeå", path: "/produktionsbolag/umea", description: "Produktionsbolag i Umeå och Västerbotten med egen regi, foto och post. Reklamfilm, brand-film, kundcase.", postalRegion: "Västerbottens län" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Produktionsbolag", path: "/produktionsbolag-film" }, { name: "Umeå", path: "/produktionsbolag/umea" }])) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      eyebrow="Produktionsbolag · Umeå"
      title={<>Produktionsbolag i Umeå — egen kedja från regi till leverans.</>}
      lede="Fenny är ett produktionsbolag i Umeå som äger hela kedjan — strategi, regi, foto, ljud, post. Tunga referenser inom tech, forskning (UmU/SLU), skogsindustri och hälsosektorn (NUS). Direkt åt varumärken eller white-label åt byråer."
      tldr={<p>Fenny är ett produktionsbolag verksamt i <strong>Umeå</strong>. Vi producerar i Holmsund, Vännäs, Robertsfors och hela Västerbotten. <strong>Fast pris</strong>, 4–6 v.</p>}
      ctaPrimary={{ label: "Få offert inom 48 h", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="produktionsbolag-umea"
      trust={umeaTrust}
      bullets={[
        { title: "Lokalt fokus", body: "Vi producerar i Holmsund, Vännäs, Robertsfors och hela Västerbotten. Resa inom regionen inkluderat." },
        { title: "Västerbotten-vana", body: "Djup erfarenhet av tech, forskning och skogsindustri." },
        { title: "Hela kedjan inhouse", body: "Strategi, regi, foto, ljud, klipp, grade. En kontaktyta — inte fem." },
      ]}
      pricing={{ heading: "Vad det kostar", intro: "Riktpriser för 2026.", rows: umeaProductionPricing, footnote: "Alla priser exkl. moms." }}
      process={{ heading: "Från brief till leverans", intro: "Linjär process med skriftligt godkännande.", steps: umeaProcess }}
      sections={[
        localProductionSection("umea", "Umeå"),
        { id: "fenny-i-staden", heading: "Fenny i Umeå", body: <p>Vi jobbar regelbundet i Umeå. För Västerbotten-kunder innebär det: skarp förplanering, koncentrerad inspelning och möjlighet att kombinera Umeå med Holmsund, Vännäs, Robertsfors och hela Västerbotten i samma produktion.</p> },
        { id: "vem", heading: "Vem vi passar för i Västerbotten", body: <p>Vi producerar för varumärken inom tech, forskning (UmU/SLU), skogsindustri och hälsosektorn (NUS) — och levererar <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/imagefilm">imagefilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/dronarfilm">drönarfilm</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/3d-animation">3D-animation</a> som faktiskt presterar.</p> },
        { id: "modeller", heading: "Hur vi levererar", body: <p>Pulse-abonnemang för löpande output, Prime för enskilda större produktioner. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">Räkna pris</a> eller jämför <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/jamfor/filmbyra-vs-frilansare">filmbyrå vs frilansare</a>.</p> },
      ]}
      faq={FAQ}
      caseLimit={6}
      related={{
        eyebrow: "Relaterat",
        title: <>Mer för {"Umeå"}-team</>,
        formats: RELATED.formats,
        siblings: RELATED.siblings,
        comparisons: RELATED.comparisons,
      }}
      closing={{ title: "Söker ni produktionsbolag i Umeå?", body: "30 minuter där vi mappar er insats och nästa steg i Västerbotten.", primary: { label: "Boka strategimöte", to: "/kontakt" }, secondary: { label: "Räkna pris", to: "/priser" } }}
    />
  );
}
